import { Inject, Injectable } from '@angular/core';

import { of, Observable, Subject } from 'rxjs';
import { delay, retryWhen, switchMap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

export interface Coordinates {
    longitude: number;
    latitude: number;
}

export interface RealtimeVehicleStatusPayload {
    regionId: number;
    topLeftCoordinates: Coordinates;
    bottomRightCoordinates: Coordinates;
}

@Injectable({
    providedIn: 'root'
})
export class WebSocketService {
    private realtimeURL: string;
    websocketUrl: string;

    private connection$: WebSocketSubject<any> | null = null;

    private readonly RETRY_SECONDS = 10;

    private isOpen = false;

    private pendingPayloads: RealtimeVehicleStatusPayload[] = [];

    /** Emits whenever a new WebSocket connection is opened (including reconnects). */
    readonly connected$ = new Subject<void>();

    constructor(@Inject('ENV') public env: any) {
        this.realtimeURL = env.REALTIME_WEBSOCKET_URL;
    }

    connect(pathname: string): Observable<any> {
        const wsUrl = this.realtimeURL + pathname;
        if (this.connection$ && this.websocketUrl !== wsUrl) {
            this.closeConnection();
        }
        this.websocketUrl = wsUrl;

        return of(this.websocketUrl).pipe(
            switchMap(() => {
                if (this.connection$) {
                    return this.connection$;
                }

                this.connection$ = webSocket({
                    url: wsUrl,

                    openObserver: {
                        next: () => {
                            this.isOpen = true;
                            this.pendingPayloads.forEach((payload) => {
                                this.connection$?.next(payload);
                            });

                            this.pendingPayloads = [];
                            // Emit to signal that the connection is open, triggering subscribers
                            // (e.g., RealtimeFacade) to send their initial payloads.
                            this.connected$.next();
                        }
                    },

                    closeObserver: {
                        next: () => {
                            this.isOpen = false;
                            this.connection$ = null;
                        }
                    },

                    deserializer: (event: MessageEvent) => {
                        try {
                            return JSON.parse(event.data);
                        } catch {
                            return event.data;
                        }
                    }
                });

                return this.connection$;
            }),
            retryWhen((errors) => errors.pipe(delay(this.RETRY_SECONDS * 1000)))
        );
    }

    send(payload: RealtimeVehicleStatusPayload): void {
        if (this.connection$ && this.isOpen) {
            this.connection$.next(payload);
            return;
        }
        this.pendingPayloads.push(payload);
    }

    closeConnection(): void {
        this.isOpen = false;
        this.pendingPayloads = [];

        if (this.connection$) {
            this.connection$.complete();
            this.connection$ = null;
        }
    }
}
